import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAblyClient } from "../contexts/ably-provider";
import { api } from "../lib/api";

export interface Notification {
	id: string;
	userId: string;
	type: string;
	title: string;
	content: string;
	isRead: boolean;
	readAt: Date | null;
	createdAt: Date;
	actionUrl?: string | null;
	metadata?: Record<string, unknown> | null;
}

interface UseNotificationsReturn {
	notifications: Notification[];
	unreadCount: number;
	isLoading: boolean;
	error: string | null;
	refetch: () => Promise<void>;
	markAsRead: (notificationId: string) => Promise<void>;
	markAllAsRead: () => Promise<void>;
}

export function useNotifications(): UseNotificationsReturn {
	const [notifications, setNotifications] = useState<Notification[]>([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const location = useLocation();

	// Get Ably client for optional real-time features
	const { client } = useAblyClient();
	const userDatabaseId = client?.auth?.clientId;

	// Deduplication helper - prevent duplicate notifications
	const addNotificationWithDeduplication = useCallback(
		(newNotification: Notification) => {
			setNotifications((prev) => {
				// Check if notification already exists
				const exists = prev.some((n) => n.id === newNotification.id);
				if (exists) {
					return prev; // Don't add duplicate
				}

				// Add new notification to the beginning of the array
				const updated = [newNotification, ...prev];

				// Calculate unread count from the updated array instead of incrementing
				const newUnreadCount = updated.filter((n) => !n.isRead).length;
				setUnreadCount(newUnreadCount);

				return updated;
			});
		},
		[],
	);

	// Set up real-time notifications using Ably client directly (no React hooks)
	useEffect(() => {
		if (!client || !userDatabaseId) {
			console.log("Ably client not available, using polling only");
			return;
		}

		let channel: ReturnType<typeof client.channels.get>;

		const setupRealtimeNotifications = async () => {
			try {
				// Get the channel using the client directly
				channel = client.channels.get(`notifications:${userDatabaseId}`);

				// Subscribe to notifications
				const messageHandler = (message: {
					data?: { type?: string; data?: Notification };
				}) => {
					if (
						message.data?.type === "notification_created" &&
						message.data?.data
					) {
						const notification = message.data.data as Notification;
						// WR-01: do NOT log notification body — title/content carry
						// PHI-adjacent strings (clinic name, event type) and would leak
						// into Sentry breadcrumbs via console capture.
						addNotificationWithDeduplication(notification);
					}
				};

				// Handle channel errors
				channel.on("failed", (stateChange) => {
					console.warn(
						"Notification channel failed, falling back to polling only:",
						stateChange,
					);
				});

				// Subscribe to notifications
				await channel.subscribe("notification_created", messageHandler);
				console.log(
					`✅ Subscribed to real-time notifications on channel: notifications:${userDatabaseId}`,
				);

				// Cleanup function
				return () => {
					if (channel) {
						channel.unsubscribe("notification_created", messageHandler);
						console.log("Unsubscribed from real-time notifications");
					}
				};
			} catch (error) {
				console.warn(
					"Failed to set up real-time notifications, using polling only:",
					error,
				);
				// Gracefully fall back to polling only - no need to throw error
				return undefined;
			}
		};

		// Set up async subscription
		const cleanupPromise = setupRealtimeNotifications();

		// Return cleanup function
		return () => {
			cleanupPromise?.then((cleanup) => cleanup?.());
		};
	}, [client, userDatabaseId, addNotificationWithDeduplication]);

	// Get current page context for chat notifications
	const getChatPageContext = useCallback(() => {
		// Chat page - mark all chat notifications as read
		if (location.pathname === "/chat") {
			return { type: "chat" };
		}
		return null;
	}, [location.pathname]);

	// Auto-mark chat message notifications as read when on chat page
	const autoMarkChatNotificationsAsRead = useCallback(
		async (notifications: Notification[]) => {
			const chatContext = getChatPageContext();
			if (!chatContext) return notifications;

			// Find unread chat message notifications
			const unreadChatNotifications = notifications.filter(
				(notification) =>
					notification.type === "message" && !notification.isRead,
			);

			if (unreadChatNotifications.length > 0) {
				const notificationIds = unreadChatNotifications.map((n) => n.id);

				try {
					// Mark them as read on the server
					await api.patch("/api/notifications/mark-read", {
						notificationIds,
					});

					// Update the notifications array to mark them as read
					return notifications.map((notification) =>
						notificationIds.includes(notification.id)
							? { ...notification, isRead: true, readAt: new Date() }
							: notification,
					);
				} catch (error) {
					console.error(
						"Error auto-marking chat notifications as read:",
						error,
					);
					return notifications;
				}
			}

			return notifications;
		},
		[getChatPageContext],
	);

	// Fetch notifications from API
	const fetchNotifications = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);

			const response = await api.get<{ notifications: Notification[] }>(
				"/api/notifications",
			);
			const rawNotifications = response.data.notifications || [];

			// Auto-mark chat notifications as read if on chat page
			const processedNotifications =
				await autoMarkChatNotificationsAsRead(rawNotifications);

			setNotifications(processedNotifications);

			// Calculate unread count
			const unreadCount = processedNotifications.filter(
				(n) => !n.isRead,
			).length;
			setUnreadCount(unreadCount);
		} catch (err) {
			console.error("Error fetching notifications:", err);
			setError(err instanceof Error ? err.message : "Unknown error");
			// Don't reset notifications on error to avoid UI flashing
		} finally {
			setIsLoading(false);
		}
	}, [autoMarkChatNotificationsAsRead]);

	// Mark a specific notification as read
	const markAsRead = useCallback(async (notificationId: string) => {
		try {
			await api.patch("/api/notifications/mark-read", {
				notificationIds: [notificationId],
			});

			// Optimistically update local state
			setNotifications((prev) =>
				prev.map((notification) =>
					notification.id === notificationId
						? { ...notification, isRead: true, readAt: new Date() }
						: notification,
				),
			);

			// Update unread count
			setUnreadCount((prev) => Math.max(0, prev - 1));
		} catch (err) {
			console.error("Error marking notification as read:", err);
		}
	}, []);

	// Mark all notifications as read
	const markAllAsRead = useCallback(async () => {
		try {
			await api.patch("/api/notifications/mark-read", {
				markAll: true,
			});

			// Optimistically update local state
			setNotifications((prev) =>
				prev.map((notification) => ({
					...notification,
					isRead: true,
					readAt: new Date(),
				})),
			);

			setUnreadCount(0);
		} catch (err) {
			console.error("Error marking all notifications as read:", err);
		}
	}, []);

	// Set up polling interval (5 minutes)
	useEffect(() => {
		// Initial fetch
		fetchNotifications();

		// Set up polling interval
		const interval = setInterval(fetchNotifications, 300000); // 5 minutes

		// Cleanup interval on unmount
		return () => clearInterval(interval);
	}, [fetchNotifications]);

	// Listen for page visibility changes to refetch when page becomes visible
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (!document.hidden) {
				fetchNotifications();
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [fetchNotifications]);

	return {
		notifications,
		unreadCount,
		isLoading,
		error,
		refetch: fetchNotifications,
		markAsRead,
		markAllAsRead,
	};
}
